import { capitalize, get } from 'lodash';

import { Operator } from './matchers/ICondition';
import { flattenElements, flattenPages, formatUniversityName, isPanel } from './form';
import {
  CurrentEnrollmentStatusType,
  EnrollmentVerificationType,
  FormComponentType,
  GPAScaleType,
  GPAWeightType,
  GradeType,
  HousingStatusType,
  IComponentSettings,
  IElementDataSource,
  IElementSchema,
  IFormSchema,
  IOption,
  IPageSchema,
  ISectionSchema,
  IValidationRuleSchema,
  IValidationSchema,
  NumberType,
  Optional,
  PathOption,
  RecommenderType,
  TreeNodeOption,
  ValidationRuleType,
  HiddenFieldType,
} from './../types/form';

import { PERSONAS_CATALOG_API_URL } from '../apis/public/master-tables';
import {
  API_URL,
  CURRENT_ENROLLMENT_STATUS_TYPE_LABELS,
  ENROLLMENT_VERIFICATION_TYPE_LABELS,
  FP_API_URL,
  GPA_SCALE_TYPE_LABELS,
  GPA_WEIGHT_TYPE_LABELS,
  HOUSING_STATUS_TYPE_LABELS,
  PATH_SEPARATOR,
  RECOMMENDER_TYPE_LABELS,
} from '../constants';
import { programLongName } from '../siteContent';
import { RoleType } from '../types/models/auth';
import { DocumentType } from '../types/models/document';
import { StageType } from '../types/models/program-management';
import { IUserProfile } from '../types/models/user-management';

/************************************
 * Validation Rule Schema Templates
 ************************************/
export const REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.REQUIRED,
  params: ['This is a required field.'],
};
export const POSITIVE_VALIDATION_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.POSITIVE,
  params: ['This field must be a positive integer.'],
};
export const INTEGER_VALIDATION_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.INTEGER,
  params: ['This field is a required field and can be positive or negative integer.'],
};
export const EMAIL_VALIDATION_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.EMAIL,
  params: ['Email format is invalid.'],
};
export const NULLABLE_VALIDATION_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.NULLABLE,
  params: [true],
};
export const PHONE_LENGTH_VALIDATION_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.MIN,
  params: [10, 'Invalid Phone Number.'],
};
export const RTE_REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.RTE_REQUIRED,
  params: ['This is a required field.'],
};
export const REPEATABLE_DEFAULT_VALUE_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.DEFAULT,
  params: [[{}]],
};

export const SSN_LENGTH_VALIDATION_RULE_SCHEMA_TEMPLATE: IValidationRuleSchema = {
  type: ValidationRuleType.MIN,
  params: [9, 'Invalid SSN Number.'],
};

const VALIDATION_RULE_SCHEMA_TEMPLATE_MAP = new Map<ValidationRuleType, any>([
  [ValidationRuleType.REQUIRED, REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
  [ValidationRuleType.RTE_REQUIRED, RTE_REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
]);

export const DEFAULT_OPTIONS_TEMPLATE: IOption[] = [
  { value: 'Option 1', label: 'Option 1' },
  { value: 'Option 2', label: 'Option 2' },
  { value: 'Option 3', label: 'Option 3' },
];

/************************************
 * Quiz Templates Field Name
 ************************************/

export const QUIZ_FIELD_NAME = 'quiz';

/************************************
 * Schema Templates
 ************************************/

const PAGE_SCHEMA_TEMPLATE: IPageSchema = {
  type: 'page',
  title: 'Page 1',
  elements: [],
};

const SECTION_SCHEMA_TEMPLATE: ISectionSchema = {
  type: 'section',
  title: 'New Section',
  pages: [
    {
      ...PAGE_SCHEMA_TEMPLATE,
    },
  ],
};

const FORM_SCHEMA_TEMPLATE: IFormSchema = {
  sections: [
    {
      ...SECTION_SCHEMA_TEMPLATE,
    },
  ],
};

const LABEL_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Label,
  id: '',
  name: '', // consider remove this
  title: 'Label',
};

const NUMBER_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Number,
  id: '',
  name: '',
  title: '',
  maxLength: 50,
  valueType: NumberType.Integer,
  validation: {
    type: 'number',
    rules: [],
  },
};

const HIDDEN_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Hidden,
  id: '',
  name: 'hidden',
  title: 'Field',
  hiddenValue: 'Value',
  hiddenFieldType: HiddenFieldType.DEFAULT,
};

const TEXT_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Text,
  id: '',
  name: '',
  title: '',
  validation: {
    type: 'string',
    rules: [],
  },
};

const TEXTAREA_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Textarea,
  id: '',
  name: '',
  title: '',
  validation: {
    type: 'string',
    rules: [],
  },
};

const CHECKBOXGROUP_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Checkboxgroup,
  id: '',
  name: '',
  title: '',
  options: [...DEFAULT_OPTIONS_TEMPLATE],
  hasCheckAll: false,
  validation: {
    type: 'array',
    rules: [],
  },
};

export const RADIOGROUP_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Radiogroup,
  id: '',
  name: '',
  title: '',
  options: [...DEFAULT_OPTIONS_TEMPLATE],
  hasOtherOption: false,
  validation: {
    type: 'string',
    rules: [],
  },
};

const DATE_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Date,
  id: '',
  name: '',
  title: '',
  validation: {
    type: 'string',
    rules: [],
  },
};

const DROPDOWN_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Dropdown,
  id: '',
  name: '',
  title: '',
  options: [...DEFAULT_OPTIONS_TEMPLATE],
  validation: {
    type: 'string',
    rules: [],
  },
};

const AUTOCOMPLETE_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.AutoComplete,
  id: '',
  name: '',
  title: '',
  maxLength: 250,
  options: [...DEFAULT_OPTIONS_TEMPLATE],
  validation: {
    type: 'string',
    rules: [],
  },
};

export const LINK_TO_PAGE_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.LinkToPage,
  name: '',
  id: '',
  linkLabel: 'Link Label',
  url: '',
};

const GRADE_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Grade,
  id: '',
  name: 'grade',
  title: 'Grade',
  valueType: GradeType.Letter,
  validation: {
    type: 'string',
    rules: [],
  },
};

const PHONE_SCHEMA_TEMPLATE: IElementSchema = {
  id: '',
  type: FormComponentType.PhoneNumber,
  name: 'phone_number',
  title: 'Phone Number',
  validation: {
    type: 'string',
    rules: [PHONE_LENGTH_VALIDATION_RULE_SCHEMA_TEMPLATE],
  },
};

const RICH_TEXT_EDITOR_SCHEMA_TEMPLATE: IElementSchema = {
  id: '',
  type: FormComponentType.RichTextEditor,
  name: 'rte_text_box',
  title: 'RTE Text Box',
  validation: {
    type: 'string',
    rules: [],
  },
};

export const RECOMMENDER_INFO_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.RecommenderInfo,
  id: '',
  name: 'recommender_info',
  title: 'Recommender Info',
  repeatable: false,
  elements: [
    {
      type: FormComponentType.Text,
      id: '',
      name: 'recommender_name',
      title: 'Recommender Name',
      placeHolder: 'Recommender Name',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'recommender_email',
      title: 'Recommender Email',
      placeHolder: 'Recommender Email',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [
          REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE,
          {
            type: ValidationRuleType.EMAIL,
            params: ['Please enter valid email.'],
          },
        ],
      },
    },
    {
      type: FormComponentType.Dropdown,
      id: '',
      name: 'recommender_type',
      title: 'Recommender Type',
      placeHolder: 'Select one',
      options: Object.values(RecommenderType).map((value) => ({ value, label: RECOMMENDER_TYPE_LABELS[value] })),
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

export const COUNSELOR_INFO_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.CounselorInfo,
  id: '',
  name: 'counselor_info',
  title: 'Counselor Info',
  repeatable: false,
  elements: [
    {
      type: FormComponentType.Text,
      id: '',
      name: 'counselor_name',
      title: 'Counselor Name',
      placeHolder: 'Counselor Name',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'counselor_email',
      title: 'Counselor Email',
      placeHolder: 'Counselor Email',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [
          REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE,
          {
            type: ValidationRuleType.EMAIL,
            params: ['Please enter valid email.'],
          },
        ],
      },
    },
  ],
};

export const ADDRESS_SCHEMA_OPTIONAL_TEMPLATE: IElementSchema = {
  type: FormComponentType.Address,
  id: '',
  name: 'address',
  title: 'Address',
  repeatable: false,
  elements: [
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_line1',
      title: 'Address line 1',
      placeHolder: 'Address line 1',
      maxLength: 50,
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_line2',
      title: 'Address line 2',
      placeHolder: 'Address line 2',
      maxLength: 50,
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_zipcode',
      title: 'Zip code',
      placeHolder: 'Zip code',
      maxLength: 50,
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_city',
      title: 'City',
      placeHolder: 'City',
      maxLength: 250,
    },
    {
      type: FormComponentType.Dropdown,
      id: '',
      name: 'address_state',
      title: 'State',
      placeHolder: 'Select one',
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_state',
      title: 'State',
      placeHolder: 'State',
      maxLength: 250,
    },
    {
      type: FormComponentType.Dropdown,
      id: '',
      name: 'address_country',
      title: 'Country',
      placeHolder: 'Select one',
    },
  ],
};

export const ADDRESS_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Address,
  id: '',
  name: 'address',
  title: 'Address',
  repeatable: false,
  elements: [
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_line1',
      title: 'Address line 1',
      placeHolder: 'Address line 1',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_line2',
      title: 'Address line 2',
      placeHolder: 'Address line 2',
      maxLength: 50,
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_zipcode',
      title: 'Zip code',
      placeHolder: 'Zip code',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_city',
      title: 'City',
      placeHolder: 'City',
      maxLength: 250,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Dropdown,
      id: '',
      name: 'address_state',
      title: 'State',
      placeHolder: 'Select one',
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'address_state',
      title: 'State',
      placeHolder: 'State',
      maxLength: 250,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Dropdown,
      id: '',
      name: 'address_country',
      title: 'Country',
      placeHolder: 'Select one',
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

const PROFILE_INFO_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.ProfileInfo,
  id: '',
  name: 'profile_info',
  title: 'Profile Info',
  canSaveToProfile: false,
  canPopulateFromProfile: false,
  elements: [
    {
      type: FormComponentType.Text,
      id: '',
      name: 'first_name',
      title: 'First Name',
      placeHolder: 'First Name',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'middle_name',
      title: 'Middle Name',
      placeHolder: 'Middle Name',
      maxLength: 50,
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'last_name',
      title: 'Last Name',
      placeHolder: 'Last Name',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

export const DATETIME_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.DateTime,
  id: '',
  name: 'datetime',
  title: 'Date and Time',
  repeatable: false,
  elements: [
    {
      type: FormComponentType.Date,
      id: '',
      name: 'date',
      title: 'Date and Time',
      showTime: true,
      validation: {
        type: 'date',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Dropdown,
      id: '',
      name: 'timezone',
      title: 'Time Zone',
      placeHolder: 'Select one',
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

export const LOCATION_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Location,
  id: '',
  name: 'location',
  title: 'Location',
  repeatable: false,
  elements: [
    {
      type: FormComponentType.Dropdown,
      id: '',
      name: 'location_country',
      title: 'Country',
      placeHolder: 'Select one',
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Dropdown,
      id: '',
      name: 'location_state',
      title: 'State',
      placeHolder: 'Select one',
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'location_state',
      title: 'State',
      placeHolder: 'State',
      maxLength: 250,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'location_city',
      title: 'City',
      placeHolder: 'City',
      maxLength: 250,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Text,
      id: '',
      name: 'location_zipcode',
      title: 'Zip code',
      placeHolder: 'Zip code',
      maxLength: 50,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

export const SSN_SCHEMA_TEMPLATE: IElementSchema = {
  id: '',
  type: FormComponentType.SSN,
  name: 'social_security_number',
  title: 'Social Security Number',
  encrypted: true,
  validation: {
    type: 'string',
    rules: [SSN_LENGTH_VALIDATION_RULE_SCHEMA_TEMPLATE],
  },
};

export const PANEL_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.Panel,
  id: '',
  name: '',
  title: '',
  repeatable: false,
  isOrderedList: false,
  elements: [],
};

export const MULTI_SELECT_PHOTOS_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.MultiSelectPhotos,
  id: '',
  name: '',
  title: '',
  dataSource: {
    url: PERSONAS_CATALOG_API_URL,
    valueName: 'id',
    titleName: 'displayText',
  },
  validation: {
    type: 'array',
    rules: [],
  },
};

const FERPA_SCHEMA_ELEMENTS: IElementSchema[] = [
  {
    type: FormComponentType.Checkbox,
    id: '',
    name: 'ferpa_acknowledge',
    title: 'Preview form to view active FERPA Statement',
    validation: {
      type: 'string',
      rules: [
        REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE,
        // Converts boolean to string of 'true' or 'false' and validates on length
        {
          type: ValidationRuleType.MIN,
          params: [4, 'This is a required field.'],
        },
        {
          type: ValidationRuleType.MAX,
          params: [4, 'This is a required field.'],
        },
      ],
    },
    hasCheckAll: false,
  },
  {
    type: FormComponentType.Hidden,
    id: '',
    name: 'ferpa_statement_id',
  },
  {
    type: FormComponentType.Hidden,
    id: '',
    name: 'ferpa_statement_text',
  },
  {
    type: FormComponentType.Text,
    id: '',
    name: 'ferpa_full_name',
    title: 'Full Name',
    placeHolder: 'Enter your full name',
    maxLength: 250,
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Date,
    id: '',
    name: 'ferpa_signature_date',
    title: "Today's Date",
    placeHolder: "Select today's date",
    validation: {
      type: 'date',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
];

export const FERPA_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.FERPAStatement,
  id: '',
  name: 'ferpa',
  title: 'FERPA Statement',
  elements: FERPA_SCHEMA_ELEMENTS,
};

export const FILE_UPLOAD_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.FileUpload,
  id: '',
  name: '',
  title: '',
  documentType: undefined,
  pullFromDocBank: true,
  validation: {
    type: 'array',
    rules: [],
  },
};

const DOCUMENT_UPLOAD_TRANSCRIPT_FORM: IElementSchema[] = [
  {
    type: FormComponentType.DocumentTypeMetadataTitle,
    id: '',
    name: '',
    title: 'Data from Transcript',
  },
  {
    type: FormComponentType.Text,
    id: '',
    name: 'institution_name',
    title: 'Institution name',
    placeHolder: 'Enter name',
    maxLength: 50,
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Dropdown,
    id: '',
    name: 'gpa_scale',
    title: 'GPA Scale',
    placeHolder: 'Select one',
    options: Object.values(GPAScaleType).map((value) => ({
      value,
      label: GPA_SCALE_TYPE_LABELS[value],
    })),
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Text,
    id: '',
    name: 'gpa_scale_details',
    title: 'GPA Scale Details',
    placeHolder: 'Enter details',
    maxLength: 250,
    visible: {
      fieldName: 'documentPanel.gpa_scale',
      operator: Operator.ONE_OF,
      value: [GPAScaleType.Written, GPAScaleType.Other],
    },
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'cumulative_gpa',
    title: 'Cumulative GPA',
    placeHolder: 'i.e. 4.0',
    maxLength: 50,
    visible: {
      fieldName: 'documentPanel.gpa_scale',
      operator: Operator.NOT_EQUAL,
      value: GPAScaleType.Written,
    },
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Dropdown,
    id: '',
    name: 'gpa_weight',
    title: 'GPA weighted or unweighted?',
    placeHolder: 'Select one',
    options: Object.values(GPAWeightType).map((value) => ({
      value,
      label: GPA_WEIGHT_TYPE_LABELS[value],
    })),
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
];

const DOCUMENT_UPLOAD_EAD_FORM: IElementSchema[] = [
  {
    type: FormComponentType.DocumentTypeMetadataTitle,
    id: '',
    name: '',
    title: 'Data from Employment Authorization Document (EAD)',
  },
  {
    type: FormComponentType.Date,
    id: '',
    name: 'expiration_date',
    title: 'Expiration Date',
    validation: {
      type: 'date',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
];

const DOCUMENT_UPLOAD_SAT_FORM = [
  {
    type: FormComponentType.DocumentTypeMetadataTitle,
    id: '',
    name: '',
    title: 'Data from SAT Scores',
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'total_score',
    title: 'Total Score',
    placeHolder: 'i.e. 1200',
    validation: {
      type: 'number',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'math_score',
    title: 'Math Score',
    placeHolder: 'i.e. 600',
    validation: {
      type: 'number',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'ebrw_score',
    title: 'EBRW (Reading & Writing) Score',
    placeHolder: 'i.e. 600',
    validation: {
      type: 'number',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
];

const DOCUMENT_UPLOAD_ACT_FORM: IElementSchema[] = [
  {
    type: FormComponentType.DocumentTypeMetadataTitle,
    id: '',
    name: '',
    title: 'Data from ACT Scores',
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'composite_score',
    title: 'Composite Score',
    placeHolder: 'i.e. 25',
    validation: {
      type: 'number',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
];

export const SECTION_HEADER_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.DocumentTypeMetadataTitle,
  name: '',
  id: '',
  title: 'section header',
};

export const DOCUMENT_UPLOAD_ENROLLMENT_VERIFICATION_FORM: IElementSchema[] = [
  {
    type: FormComponentType.DocumentTypeMetadataTitle,
    id: '',
    name: '',
    title: 'Data from Enrollment Verification (EV)',
  },
  {
    type: FormComponentType.AutoComplete,
    id: '',
    name: 'university_name',
    title: 'University Name',
    placeHolder: '',
    preventSearchStringSave: true,
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
    dataSource: {
      url: `${API_URL}/catalog-api?name=university`,
      valueName: 'id',
      titleName: 'displayText',
      transformTitle: formatUniversityName,
    },
  },
  {
    type: FormComponentType.Date,
    id: '',
    name: 'term_start_date',
    title: 'Term Start Date',
    placeHolder: 'Term Start Date',
    validation: {
      type: 'date',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Dropdown,
    id: '',
    name: 'ev_form_type',
    title: 'Enrollment Verification Form Type',
    placeHolder: 'Select one',
    options: Object.values(EnrollmentVerificationType).map((value) => ({
      value,
      label: ENROLLMENT_VERIFICATION_TYPE_LABELS[value],
    })),
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Dropdown,
    id: '',
    name: 'current_enrollment_status',
    title: 'Current Enrollment Status',
    placeHolder: 'Select one',
    options: Object.values(CurrentEnrollmentStatusType).map((key) => ({
      value: CURRENT_ENROLLMENT_STATUS_TYPE_LABELS[key],
      label: CURRENT_ENROLLMENT_STATUS_TYPE_LABELS[key],
    })),
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
];

const DOCUMENT_UPLOAD_SAR_FORM: IElementSchema[] = [
  {
    type: FormComponentType.DocumentTypeMetadataTitle,
    id: '',
    name: '',
    title: '',
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'efc_amount',
    title: 'Student Aid Index',
    placeHolder: 'i.e. $500',
    validation: {
      type: 'number',
      rules: [POSITIVE_VALIDATION_RULE_SCHEMA_TEMPLATE, REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
    currencySymbol: '$',
  },
];

export const DOCUMENT_UPLOAD_FAAL_FORM: IElementSchema[] = [
  {
    type: FormComponentType.DocumentTypeMetadataTitle,
    id: '',
    name: '',
    title: 'Data from Financial Aid Award Letter (FAAL)',
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'total_gift_aid_amount',
    title: 'Total Gift Aid Amount',
    validation: {
      type: 'number',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
    currencySymbol: '$',
    placeHolder: 'i.e. $500',
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'total_work_study_amount',
    title: 'Total Work Study Amount',
    validation: {
      type: 'number',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
    currencySymbol: '$',
    placeHolder: 'i.e. $500',
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'total_cost_of_attendance_amount',
    title: 'Total Cost of Attendance Amount',
    validation: {
      type: 'number',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
    placeHolder: 'i.e. $220',
    currencySymbol: '$',
  },
  {
    type: FormComponentType.Text,
    id: '',
    name: 'source_url_for_total_cost_of_attendance',
    title: 'Source URL for Total Cost of Attendance',
    placeHolder: 'Enter URL',
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
  {
    type: FormComponentType.Number,
    id: '',
    name: 'efc_amount',
    title: 'SAI Amount',
    validation: {
      type: 'number',
      rules: [POSITIVE_VALIDATION_RULE_SCHEMA_TEMPLATE, REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
    currencySymbol: '$',
    placeHolder: 'i.e. $500',
  },
  {
    type: FormComponentType.AutoComplete,
    id: '',
    name: 'institution_name',
    title: 'Institution Name ',
    placeHolder: '',
    preventSearchStringSave: true,
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
    dataSource: {
      url: `${API_URL}/catalog-api?name=university`,
      valueName: 'id',
      titleName: 'displayText',
      transformTitle: formatUniversityName,
    },
  },
  {
    type: FormComponentType.Dropdown,
    id: '',
    name: 'housing_status',
    title: 'Housing Status',
    placeHolder: 'Select one',
    options: Object.values(HousingStatusType).map((value) => ({ value, label: HOUSING_STATUS_TYPE_LABELS[value] })),
    validation: {
      type: 'string',
      rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
    },
  },
];

export const DOCUMENT_UPLOAD_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.DocumentUploadPanel,
  id: '',
  name: 'documentPanel',
  title: '',
  repeatable: false,
  documentType: undefined,
  validationRequired: true,
  elements: [
    {
      type: FormComponentType.FileUpload,
      id: '',
      name: 'fileUpload',
      title: '',
      documentType: undefined,
      pullFromDocBank: true,
      validation: {
        type: 'array',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

const READER_QUIZ_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.ReaderQuiz,
  id: '',
  name: QUIZ_FIELD_NAME,
  title: ' ',
  elements: [
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_1',
      title:
        '1. Which of the following types of questions should you expect to answer about the applicant and their essays?',
      options: [
        {
          value: 'Questions about grammar and mechanics',
          label: 'Questions about grammar and mechanics',
        },
        {
          value: "Questions about the applicant's voice and focus",
          label: "Questions about the applicant's voice and focus",
        },
        {
          value: 'Questions about your overall impression of the applicant',
          label: 'Questions about your overall impression of the applicant',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_2',
      title:
        '2. When you log in to your Reader Dashboard, you will see a blue button with the words "Request a Read." What happens when you click on the button?',
      options: [
        {
          value: 'A pop-up appears asking you to select the type of student essay you would like to evaluate',
          label: 'A pop-up appears asking you to select the type of student essay you would like to evaluate',
        },
        {
          value:
            'It refreshes your "Reader Dashboard" so that you know how many more applications you have left to read',
          label:
            'It refreshes your "Reader Dashboard" so that you know how many more applications you have left to read',
        },
        {
          value: 'It provides you with more time to read your remaining scholarship applications',
          label: 'It provides you with more time to read your remaining scholarship applications',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'A pop-up appears asking you to select the type of student essay you would like to evaluate',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_3',
      title: '3. As a reader, you should select "Flag Essay" if:',
      options: [
        {
          value: 'You believe that you know the applicant',
          label: 'You believe that you know the applicant',
        },
        {
          value: 'You believe the essay is plagiarized',
          label: 'You believe the essay is plagiarized',
        },
        {
          value: "You believe that you will not be able to fairly score an applicant's essays",
          label: "You believe that you will not be able to fairly score an applicant's essays",
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_4',
      title: `4. The ${programLongName} is looking for students who have excelled both in and out of the classroom. Which of the following are characteristics of a ${programLongName} Scholar?`,
      options: [
        {
          value: 'Leadership',
          label: 'Leadership',
        },
        {
          value: 'Creativity',
          label: 'Creativity',
        },
        {
          value: 'Perseverance',
          label: 'Perseverance',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_5',
      title: '5. Which of the following can you NOT do via your Reader Dashboard?',
      options: [
        {
          value: "View an applicant's personal information, including name and institution",
          label: "View an applicant's personal information, including name and institution",
        },
        {
          value: "Add notes to specific sections of an applicant's essays",
          label: "Add notes to specific sections of an applicant's essays",
        },
        {
          value: 'Send a message directly to the applicant to seek clarification',
          label: 'Send a message directly to the applicant to seek clarification',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_6',
      title: "6. What information will the reader have access to when reading an applicant's essays?",
      options: [
        {
          value: "The applicant's personal information (name and address)",
          label: "The applicant's personal information (name and address)",
        },
        {
          value: 'This is a blind read therefore no information about the applicant will be available',
          label: 'This is a blind read therefore no information about the applicant will be available',
        },
        {
          value: 'The name of the college/university that the applicant plans to attend in the fall',
          label: 'The name of the college/university that the applicant plans to attend in the fall',
        },
        {
          value: "The applicant's entire scholarship application",
          label: "The applicant's entire scholarship application",
        },
      ],
      answer: 'This is a blind read therefore no information about the applicant will be available',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_7',
      title: `7. When a reader select "Not Applicable" as a response to one, or more, of the evaluation questions, what happens to an applicant's essays?`,
      options: [
        {
          value: 'The applicant will not get points for that evaluation area',
          label: 'The applicant will not get points for that evaluation area',
        },
        {
          value: `The application system will notify The Scholarship Team to review the essay`,
          label: `The application system will notify The Scholarship Team to review the essay`,
        },
        {
          value: '"Not Applicable" is not a response option',
          label: '"Not Applicable" is not a response option',
        },
        {
          value: 'Both A and B',
          label: 'Both A and B',
        },
      ],
      answer: '"Not Applicable" is not a response option',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_8',
      title: '8. During the "Review & Submit" screen, you will be able to:',
      options: [
        {
          value: 'Review your entire evaluation on one page',
          label: 'Review your entire evaluation on one page',
        },
        {
          value: 'Edit previous answers',
          label: 'Edit previous answers',
        },
        {
          value: 'Save your answers to complete at a later time',
          label: 'Save your answers to complete at a later time',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_9',
      title: '9. Once you have evaluated 25 applications, you may:',
      options: [
        {
          value: 'Go back to your Reader Dashboard to read more',
          label: 'Go back to your Reader Dashboard to read more',
        },
        {
          value:
            'Share your reader experience with friends and colleagues and encourage them to get involved in future cycles',
          label:
            'Share your reader experience with friends and colleagues and encourage them to get involved in future cycles',
        },
        {
          value: `Edit previously submitted evaluations`,
          label: `Edit previously submitted evaluations`,
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer:
        'Share your reader experience with friends and colleagues and encourage them to get involved in future cycles',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_10',
      title: `10. Have you downloaded Reader Calibration Packet and read through the material?`,
      options: [
        {
          value: 'Yes, I have read and internalized the content',
          label: 'Yes, I have read and internalized the content',
        },
        {
          value: 'No, I have not opened/read the document',
          label: 'No, I have not opened/read the document',
        },
      ],
      answer: 'Yes, I have read and internalized the content',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

// TODO: update with final question and answer
const COACH_QUIZ_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.CoachQuiz,
  id: '',
  name: QUIZ_FIELD_NAME,
  title: ' ',
  elements: [
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_1',
      title: `1. The intention of the ${programLongName} Career Coach program is to:`,
      options: [
        {
          value: 'Provide advice and guidance to students seeking assistance with their resume',
          label: 'Provide advice and guidance to students seeking assistance with their resume',
        },
        {
          value: 'Share knowledge and insight to help students master their job interview',
          label: 'Share knowledge and insight to help students master their job interview',
        },
        {
          value: 'Advise students how to strengthen their negotiation skills',
          label: 'Advise students how to strengthen their negotiation skills',
        },
        {
          value: 'Provide Career Coaches the opportunity to support young leaders',
          label: 'Provide Career Coaches the opportunity to support young leaders',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_2',
      title: '2. The role of a Career Coach is to:',
      options: [
        {
          value: 'Complete a coaching assignment within a specified amount of time, typically no more than 2 hours',
          label: 'Complete a coaching assignment within a specified amount of time, typically no more than 2 hours',
        },
        {
          value: 'Provide a thorough written student evaluation at the end of each coaching assignment ',
          label: 'Provide a thorough written student evaluation at the end of each coaching assignment ',
        },
        {
          value: 'Complete a brief survey at the end of each coaching assignment',
          label: 'Complete a brief survey at the end of each coaching assignment',
        },
        {
          value: 'A and C',
          label: 'A and C',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'A and C',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_3',
      title: '3. It is recommended that interactions between a career coach and student take place:',
      options: [
        {
          value: 'Only in-person',
          label: 'Only in-person',
        },
        {
          value: 'Via videoconference',
          label: 'Via videoconference',
        },
        {
          value: 'On the phone',
          label: 'On the phone',
        },
        {
          value: 'Via postcard',
          label: 'Via postcard',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'Via videoconference',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_4',
      title: '4. After being notified of an assignment, a Career Coach is required to contact the student within:',
      options: [
        {
          value: 'Two weeks',
          label: 'Two weeks',
        },
        {
          value: 'Three months',
          label: 'Three months',
        },
        {
          value: '48 hours',
          label: '48 hours',
        },
        {
          value: 'Within 5 minutes',
          label: 'Within 5 minutes',
        },
        {
          value: 'None of the above',
          label: 'None of the above',
        },
      ],
      answer: '48 hours',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_5',
      title: '5. When scheduling the coaching session, you should consider:',
      options: [
        {
          value: 'Your mentees class schedule',
          label: 'Your mentees class schedule',
        },
        {
          value: 'Availability for meetings after 10pm',
          label: 'Availability for meetings after 10pm',
        },
        {
          value: 'Possible difference in time zones',
          label: 'Possible difference in time zones',
        },
        {
          value: 'Both A and C',
          label: 'Both A and C',
        },
        {
          value: 'None of the above',
          label: 'None of the above',
        },
      ],
      answer: 'Both A and C',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_6',
      title: '6. Which of the following are appropriate in-person meeting locations?',
      options: [
        {
          value: 'Private work office',
          label: 'Private work office',
        },
        {
          value: 'Your home',
          label: 'Your home',
        },
        {
          value: "An applicant's home",
          label: "An applicant's home",
        },
        {
          value: 'A public location e.g. local coffee shop',
          label: 'A public location e.g. local coffee shop',
        },
        {
          value: 'None of the above',
          label: 'None of the above',
        },
      ],
      answer: 'A public location e.g. local coffee shop',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_7',
      title: '7. Coaches are expected to unsolicited advice on a wide range of topics during each coaching assignment.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'False',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_8',
      title: '8. Coaches and students will complete a brief evaluation survey at the end of each coaching session.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'True',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_9',
      title: '9. Coaches are expected to spend a minimum of 20 hours preparing for each coaching assignment.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'False',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_10',
      title:
        '10. Coaches should feel comfortable meeting with students at either their residence or the residence of the student.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'False',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

const EXTRA_ELEMENTS_TEMPLATE_MAP = new Map<any, IElementSchema[]>([
  [DocumentType.ACT, DOCUMENT_UPLOAD_ACT_FORM],
  [DocumentType.SAT, DOCUMENT_UPLOAD_SAT_FORM],
  [DocumentType.TRANSCRIPT, DOCUMENT_UPLOAD_TRANSCRIPT_FORM],
  [DocumentType.EMPLOYMENT_AUTHORIZATION_DOCUMENT, DOCUMENT_UPLOAD_EAD_FORM],
  [DocumentType.ENROLLMENT_VERIFICATION, DOCUMENT_UPLOAD_ENROLLMENT_VERIFICATION_FORM],
  [DocumentType.STUDENT_AID_REPORT, DOCUMENT_UPLOAD_SAR_FORM],
  [DocumentType.FINANCIAL_AID_AWARD_LETTER, DOCUMENT_UPLOAD_FAAL_FORM],
]);

const INTERVIEWER_QUIZ_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.InterviewerQuiz,
  id: '',
  name: QUIZ_FIELD_NAME,
  title: ' ',
  elements: [
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_1',
      title: `1. The interview for the ${programLongName} is meant to:`,
      options: [
        { value: 'Empower', label: 'Empower' },
        {
          value: 'Evaluate',
          label: 'Evaluate',
        },
        {
          value: 'Discourage',
          label: 'Discourage',
        },
        {
          value: 'Both A and B',
          label: 'Both A and B',
        },
        {
          value: 'None of the above',
          label: 'None of the above',
        },
      ],
      answer: 'Both A and B',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_2',
      title: `2. The interviews for the ${programLongName} can take place:`,
      options: [
        { value: 'In-person', label: 'In-person' },
        {
          value: 'Via video chat',
          label: 'Via video chat',
        },
        {
          value: 'Over the phone',
          label: 'Over the phone',
        },
        {
          value: 'Both A and B',
          label: 'Both A and B',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'Both A and B',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_3',
      title: '3. After being assigned interviewees, you are required to contact your assigned student within:',
      options: [
        { value: '48 hours', label: '48 hours' },
        {
          value: '72 hours',
          label: '72 hours',
        },
        {
          value: 'One work week',
          label: 'One work week',
        },
        {
          value: 'Anytime during the month of March',
          label: 'Anytime during the month of March',
        },
        {
          value: 'None of the above',
          label: 'None of the above',
        },
      ],
      answer: '48 hours',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_4',
      title: '4. When establishing an interview date and time, you should consider:',
      options: [
        {
          value: "A student's availability outside of school hours",
          label: "A student's availability outside of school hours",
        },
        {
          value: "If applicable, a venue's accessibility for the student",
          label: "If applicable, a venue's accessibility for the student",
        },
        {
          value: "A parent's availability to participate in the interview process",
          label: "A parent's availability to participate in the interview process",
        },
        {
          value: 'Both A and B',
          label: 'Both A and B',
        },
        {
          value: 'Both A and C',
          label: 'Both A and C',
        },
      ],
      answer: 'Both A and B',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_5',
      title: '5. Which of the following is an appropriate interview location?',
      options: [
        {
          value: 'Private work office',
          label: 'Private work office',
        },
        {
          value: 'Your home',
          label: 'Your home',
        },
        {
          value: "An applicant's home",
          label: "An applicant's home",
        },
        {
          value: 'A local coffee shop',
          label: 'A local coffee shop',
        },
        {
          value: 'None of the above',
          label: 'None of the above',
        },
      ],
      answer: 'A local coffee shop',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_6',
      title: '6. Candidates will be evaluated on the following criteria:',
      options: [
        {
          value: 'Engagement',
          label: 'Engagement',
        },
        {
          value: 'Emotional Maturity',
          label: 'Emotional Maturity',
        },
        {
          value: 'Critical Thinking',
          label: 'Critical Thinking',
        },
        {
          value: 'Leadership/Service',
          label: 'Leadership/Service',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_7',
      title:
        '7. Interview questions will be provided prior to the interview and must be used in order to effectively evaluate a candidate.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'True',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_8',
      title: `8. Interviewers must mail in a written interview report to the ${programLongName} via US postal services by March 31.`,
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'False',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_9',
      title:
        '9. As an interviewer, you should feel comfortable driving a student back to his/her home after the interview.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'False',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

const MENTOR_QUIZ_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.MentorQuiz,
  id: '',
  name: QUIZ_FIELD_NAME,
  title: ' ',
  elements: [
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_1',
      title: `1. The intention of the ${programLongName} MentorMatch program is to:`,
      options: [
        {
          value: 'Advance personal and professional development',
          label: 'Advance personal and professional development',
        },
        {
          value: 'Provide students with a specific, regimented path to success',
          label: 'Provide students with a specific, regimented path to success',
        },
        {
          value:
            'Foster the exchange of knowledge, experience, and advice between experienced professionals and determined young leaders',
          label:
            'Foster the exchange of knowledge, experience, and advice between experienced professionals and determined young leaders',
        },
        {
          value: 'Fulfill required services hours for mentees and mentors',
          label: 'Fulfill required services hours for mentees and mentors',
        },
        {
          value: 'Both A and C',
          label: 'Both A and C',
        },
      ],
      answer: 'Both A and C',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_2',
      title: `2. The role of an ${programLongName} Mentor is to:`,
      options: [
        {
          value: 'Be a trusted advisor',
          label: 'Be a trusted advisor',
        },
        {
          value: 'Serve as a role model',
          label: 'Serve as a role model',
        },
        {
          value: 'Support and encourage their mentees',
          label: 'Support and encourage their mentees',
        },
        {
          value: 'Help mentees improve their skills and advance their careers',
          label: 'Help mentees improve their skills and advance their careers',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_3',
      title: '3. Interactions between a mentor and mentee can take place:',
      options: [
        {
          value: 'In-person',
          label: 'In-person',
        },
        {
          value: 'Via videoconference',
          label: 'Via videoconference',
        },
        {
          value: 'Through email or text',
          label: 'Through email or text',
        },
        {
          value: 'On the phone',
          label: 'On the phone',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'All of the above',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_4',
      title: '4. After being assigned a mentee, you are required to contact your assigned mentee within:',
      options: [
        {
          value: 'Two weeks',
          label: 'Two weeks',
        },
        {
          value: 'Three months',
          label: 'Three months',
        },
        {
          value: '48 hours',
          label: '48 hours',
        },
        {
          value: 'Immediately',
          label: 'Immediately',
        },
        {
          value: 'None of the above',
          label: 'None of the above',
        },
      ],
      answer: 'Two weeks',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_5',
      title: '5. When establishing a regular meeting schedule with your mentee, you should consider:',
      options: [
        {
          value: 'Your mentees class schedule',
          label: 'Your mentees class schedule',
        },
        {
          value: 'Availability of your mentee for meetings after 9pm',
          label: 'Availability of your mentee for meetings after 9pm',
        },
        {
          value: 'Your mentee’s access to transportation, if you schedule in person meetings',
          label: 'Your mentee’s access to transportation, if you schedule in person meetings',
        },
        {
          value: 'Both A and C',
          label: 'Both A and C',
        },
        {
          value: 'None of the above',
          label: 'None of the above',
        },
      ],
      answer: 'Both A and C',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_6',
      title: '6. Which of the following are appropriate in-person meeting locations?',
      options: [
        {
          value: 'Private work office',
          label: 'Private work office',
        },
        {
          value: 'Your home',
          label: 'Your home',
        },
        {
          value: 'An applicant’s home',
          label: 'An applicant’s home',
        },
        {
          value: 'A public location e.g. local coffee shop',
          label: 'A public location e.g. local coffee shop',
        },
        {
          value: 'All of the above',
          label: 'All of the above',
        },
      ],
      answer: 'A public location e.g. local coffee shop',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_7',
      title: '7. Mentors and Mentees will be guided through the MentorMatch experience by a set of 10 milestones.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'True',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_8',
      title: '8. Mentors and Mentees will complete a brief evaluation survey once all 10 milestones are completed.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'True',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_9',
      title: '9. Mentors must write and submit a formatted report for each interaction with their mentee.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'False',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
    {
      type: FormComponentType.Radiogroup,
      id: '',
      name: 'question_10',
      title:
        '10. Mentors should feel comfortable meeting with mentees at either their residence or the residence of the mentee.',
      options: [
        {
          value: 'True',
          label: 'True',
        },
        {
          value: 'False',
          label: 'False',
        },
      ],
      answer: 'False',
      hasOtherOption: false,
      validation: {
        type: 'string',
        rules: [REQUIRED_VALIDATION_RULE_SCHEMA_TEMPLATE],
      },
    },
  ],
};

export const formatUniversityFederalCode = (_: any, { federalCode, schoolName, city, state }: any) =>
  `${federalCode} | ${schoolName}${city ? ' | ' + capitalize(city.toLowerCase()) : ''}${state ? ',' + state : ''}`;

export const FINANCIAL_PORTAL_FEDERAL_CODE = 'institution_federal_code';

const FINANCIAL_PORTAL_SCHEMA_TEMPLATE: IElementSchema = {
  type: FormComponentType.FinancialPortal,
  id: '',
  name: 'financial_portal',
  title: '',
  elements: [
    {
      hiddenFieldType: HiddenFieldType.USER_ROLE,
      hiddenValue: RoleType.SCHOLAR,
      id: '',
      name: 'role_assignment',
      title: 'Role Assignment',
      type: FormComponentType.Hidden,
    },
    {
      type: FormComponentType.AutoComplete,
      id: '',
      name: FINANCIAL_PORTAL_FEDERAL_CODE,
      title: 'Institution Name',
      instructiveLabel:
        'If your school does not show up in this list, contact the admins for assistance, and provide them with your school name, campus, and any additional information.',
      preventSearchStringSave: true,
      dataSource: {
        url: `${FP_API_URL}/filter-university`,
        valueName: 'federalCode',
        titleName: 'schoolName',
        transformTitle: formatUniversityFederalCode,
      },
    },
    {
      type: FormComponentType.LinkToPage,
      id: '',
      name: 'find_federal_code',
      linkLabel: 'Look up your Fed Code',
      url: 'https://studentaid.gov/fafsa-app/FSCsearch',
    },
    ...[FERPA_SCHEMA_TEMPLATE],
  ],
};

const COMPONENT_SCHEMA_TEMPLATE_MAP = new Map<FormComponentType, IElementSchema>([
  [FormComponentType.Address, ADDRESS_SCHEMA_TEMPLATE],
  [FormComponentType.Checkboxgroup, CHECKBOXGROUP_SCHEMA_TEMPLATE],
  [FormComponentType.Panel, PANEL_SCHEMA_TEMPLATE],
  [FormComponentType.Date, DATE_SCHEMA_TEMPLATE],
  [FormComponentType.Dropdown, DROPDOWN_SCHEMA_TEMPLATE],
  [FormComponentType.Label, LABEL_SCHEMA_TEMPLATE],
  [FormComponentType.Number, NUMBER_SCHEMA_TEMPLATE],
  [FormComponentType.ProfileInfo, PROFILE_INFO_SCHEMA_TEMPLATE],
  [FormComponentType.RecommenderInfo, RECOMMENDER_INFO_SCHEMA_TEMPLATE],
  [FormComponentType.CounselorInfo, COUNSELOR_INFO_SCHEMA_TEMPLATE],
  [FormComponentType.Radiogroup, RADIOGROUP_SCHEMA_TEMPLATE],
  [FormComponentType.Text, TEXT_SCHEMA_TEMPLATE],
  [FormComponentType.Textarea, TEXTAREA_SCHEMA_TEMPLATE],
  [FormComponentType.Grade, GRADE_SCHEMA_TEMPLATE],
  [FormComponentType.PhoneNumber, PHONE_SCHEMA_TEMPLATE],
  [FormComponentType.RichTextEditor, RICH_TEXT_EDITOR_SCHEMA_TEMPLATE],
  [FormComponentType.DateTime, DATETIME_SCHEMA_TEMPLATE],
  [FormComponentType.AutoComplete, AUTOCOMPLETE_SCHEMA_TEMPLATE],
  [FormComponentType.LinkToPage, LINK_TO_PAGE_SCHEMA_TEMPLATE],
  [FormComponentType.Location, LOCATION_SCHEMA_TEMPLATE],
  [FormComponentType.SSN, SSN_SCHEMA_TEMPLATE],
  [FormComponentType.MultiSelectPhotos, MULTI_SELECT_PHOTOS_SCHEMA_TEMPLATE],
  [FormComponentType.FileUpload, FILE_UPLOAD_SCHEMA_TEMPLATE],
  [FormComponentType.DocumentUploadPanel, DOCUMENT_UPLOAD_SCHEMA_TEMPLATE],
  [FormComponentType.DocumentTypeMetadataTitle, SECTION_HEADER_SCHEMA_TEMPLATE],
  [FormComponentType.ReaderQuiz, READER_QUIZ_SCHEMA_TEMPLATE],
  [FormComponentType.CoachQuiz, COACH_QUIZ_SCHEMA_TEMPLATE],
  [FormComponentType.InterviewerQuiz, INTERVIEWER_QUIZ_SCHEMA_TEMPLATE],
  [FormComponentType.MentorQuiz, MENTOR_QUIZ_SCHEMA_TEMPLATE],
  [FormComponentType.Hidden, HIDDEN_SCHEMA_TEMPLATE],
  [FormComponentType.FERPAStatement, FERPA_SCHEMA_TEMPLATE],
  [FormComponentType.FinancialPortal, FINANCIAL_PORTAL_SCHEMA_TEMPLATE],
]);

const COMPONENT_VISIBLE_OPERATORS_MAP = new Map<FormComponentType, Operator[]>([
  [FormComponentType.Text, [Operator.EQUAL, Operator.NOT_EQUAL]],
  [FormComponentType.Textarea, [Operator.EQUAL, Operator.NOT_EQUAL]],
  [FormComponentType.Checkboxgroup, [Operator.CONTAINS, Operator.NOT_CONTAINS]],
  [FormComponentType.Radiogroup, [Operator.EQUAL, Operator.NOT_EQUAL, Operator.ONE_OF]],
  [FormComponentType.Dropdown, [Operator.EQUAL, Operator.NOT_EQUAL]],
  [FormComponentType.AutoComplete, [Operator.EQUAL, Operator.NOT_EQUAL]],
  [FormComponentType.Date, [Operator.EQUAL, Operator.NOT_EQUAL, Operator.GREATER_THAN, Operator.LESS_THAN]],
  [FormComponentType.Number, [Operator.EQUAL, Operator.NOT_EQUAL, Operator.GREATER_THAN, Operator.LESS_THAN]],
  [FormComponentType.Grade, [Operator.EQUAL, Operator.NOT_EQUAL, Operator.GREATER_THAN, Operator.LESS_THAN]],
]);

const COMPONENT_VALUE_TYPE_OPTIONS_MAP = new Map<FormComponentType, IOption[]>([
  [
    FormComponentType.Number,
    Object.values(NumberType).map((value) => {
      return { label: capitalize(value), value };
    }),
  ],
  [
    FormComponentType.Grade,
    [
      { label: 'Only letters', value: GradeType.Letter },
      { label: 'Only numbers', value: GradeType.Number },
    ],
  ],
]);

/**
 * This constant manages the component types we support in the "this Question" dropdown for visibility setting
 */
export const VISIBLE_COMPONENT_TYPES = [...COMPONENT_VISIBLE_OPERATORS_MAP.keys()].concat(FormComponentType.Panel);

const AVAILABLE_COMPONENT_TYPES_BY_STAGE_TYPE = new Map<StageType, FormComponentType[]>([
  [StageType.READER_CERTIFICATION, [FormComponentType.ReaderQuiz]],
  [StageType.COACH_CERTIFICATION, [FormComponentType.CoachQuiz]],
  [StageType.INTERVIEWER_CERTIFICATION, [FormComponentType.InterviewerQuiz]],
  [StageType.MENTOR_CERTIFICATION, [FormComponentType.MentorQuiz]],
]);

/**
 * Generates a random id string
 * @example
 *  '1nwpmoixs9o'
 *  '1pffgn7kntd'
 *  '9dbk6m9u3zk'
 *  '25997nm11wg'
 *  '815cnujlhg'
 */
export const generateRandomId = () => (Number(String(Math.random()).slice(2)) + Date.now()).toString(36);

/**
 * Generates and updates the id field(s) in the given element schema, and the corresponding ids of the elements if exist.
 *
 * @param {IElementSchema} schema Element schema
 */
export const generateIdsForElementSchema = (schema: IElementSchema) => {
  schema.id = generateRandomId();

  if (isPanel(schema.type)) {
    schema.elements?.forEach(generateIdsForElementSchema);
  }
};

/**
 * Deep clone the given @param json object.
 * Note,
 *  - Does not support field types such as function, date, symbols
 *  - Objects such as Date will be serialized
 *
 * @param {object} json Object to be cloned consist of plain objects as fields
 * @return {object} Cloned json
 */
const cloneJson = (json: any) => JSON.parse(JSON.stringify(json));

export const getInitialFormSchema = (): IFormSchema => cloneJson(FORM_SCHEMA_TEMPLATE);

export const getSectionSchema = (): ISectionSchema => cloneJson(SECTION_SCHEMA_TEMPLATE);

export const getPageSchema = (): IPageSchema => cloneJson(PAGE_SCHEMA_TEMPLATE);

/**
 * Get the corresponding validation rule json schema for the given @param type
 *
 * @param {ValidationRuleType} type Validation rule type (i.e. ValidationRuleType)
 * @return {IValidationRuleSchema} Returns the json schema or undefined if does not exist
 */
export const getValidationRuleSchemaForType = (type: ValidationRuleType): IValidationRuleSchema => {
  const schema = VALIDATION_RULE_SCHEMA_TEMPLATE_MAP.get(type);
  if (!schema) {
    throw Error(`Not implemented for schema type: ${type}`);
  }
  return cloneJson(schema);
};

const changeConfigOfElements = (
  elements: IElementSchema[],
  validationRequired?: boolean,
  allowNegativeValue?: boolean
): IElementSchema[] => {
  const removerRequired = (rules: any) => {
    // TODO Check this logic because
    // it seems to be wrong using || instead of &&
    // Additionally it should't be assignment anything to rules as it's a function's param
    return (rules = rules.filter(({ type }: any) => {
      type !== ValidationRuleType.RTE_REQUIRED || type !== ValidationRuleType.REQUIRED;
    }));
  };
  if (validationRequired !== undefined) {
    const elementConfig = elements.map<IElementSchema>((element: IElementSchema) => {
      let rules = element.validation?.rules || [];
      if (element.name === 'efc_amount') {
        if (!validationRequired) {
          rules = rules.filter(
            (rule) => rule.type !== ValidationRuleType.RTE_REQUIRED && rule.type !== ValidationRuleType.REQUIRED
          );
        }
        if (allowNegativeValue) {
          rules = rules.filter((rule) => rule.type !== ValidationRuleType.POSITIVE);
        }
      } else {
        if (!validationRequired) {
          rules = removerRequired(rules);
        } else {
          rules = removerRequired(rules);
          const validationRuleSchema = getValidationRuleSchemaForType(ValidationRuleType.REQUIRED);
          rules.push(validationRuleSchema);
        }
      }
      const newElement: IElementSchema = {
        ...element,
        validation: {
          type: element.validation?.type || '',
          rules,
        },
      };
      return newElement;
    });
    return elementConfig;
  }
  return elements;
};

/**
 * Get the corresponding element json schema for the given @param type with id's generated
 *
 * @param {FormComponentType} type Form component type (i.e. FormComponentType)
 * @return {IElementSchema} Returns the json schema
 */
export const getElementSchemaForType = (
  type: FormComponentType,
  elementsKey?: any,
  validationRequired?: boolean,
  isOptionalPanel?: boolean,
  allowNegativeValue?: boolean
): IElementSchema => {
  const componentTemplate =
    type === FormComponentType.Address && isOptionalPanel === true
      ? ADDRESS_SCHEMA_OPTIONAL_TEMPLATE
      : COMPONENT_SCHEMA_TEMPLATE_MAP.get(type);
  const additionalElements = EXTRA_ELEMENTS_TEMPLATE_MAP.get(elementsKey);
  let newElements = elementsKey && [
    ...(componentTemplate?.elements ? componentTemplate.elements : []),
    ...(additionalElements ? additionalElements : []),
  ];
  newElements = changeConfigOfElements(newElements, validationRequired, allowNegativeValue);
  const schemaTemplate = elementsKey
    ? {
        ...componentTemplate,
        elements: newElements,
      }
    : componentTemplate;
  if (!schemaTemplate) {
    throw Error(`Not implemented for schema type: ${type}`);
  }

  const schema = cloneJson(schemaTemplate);

  generateIdsForElementSchema(schema);

  if (get(schema, 'validation.rules')) {
    const isRTE = type === FormComponentType.RichTextEditor;
    const requiredRuleSchemaTemplate = isRTE
      ? getValidationRuleSchemaForType(ValidationRuleType.RTE_REQUIRED)
      : getValidationRuleSchemaForType(ValidationRuleType.REQUIRED);
    if (!requiredRuleSchemaTemplate) {
      return schema;
    }

    const requiredRuleSchema = cloneJson(requiredRuleSchemaTemplate);
    schema.validation.rules = [...schema.validation.rules, requiredRuleSchema];
  }
  return schema;
};

/**
 * Get the corresponding supported visible setting operators for the given @param type
 *
 * @param {FormComponentType} type Form component type (i.e. FormComponentType)
 * @return {Operator[]} Returns the list of operator
 */
export const getOperatorsForType = (type: FormComponentType): Operator[] | undefined => {
  const operators = COMPONENT_VISIBLE_OPERATORS_MAP.get(type);
  return operators;
};

/**
 * Get the enum for the given @param type
 *
 * @param {FormComponentType[]} type Form component type (i.e. FormComponentType[])
 * @return {string[]} Returns the list of `valueTypes`
 */
export const getValueTypeForType = (type: FormComponentType): IOption[] | undefined => {
  const valueTypes = COMPONENT_VALUE_TYPE_OPTIONS_MAP.get(type);
  return valueTypes;
};

/**
 * Finds whether the given validation object contains the given rule type.
 *
 * @param {ValidationRuleType} ruleType Type of the rule
 * @param {IValidationSchema} validation Validation schema object
 * @return {boolean} Returns true if the validation has the given rule type
 */
export const hasValidationType = (ruleType: ValidationRuleType, validation?: IValidationSchema): boolean =>
  validation !== undefined && !!validation.rules.find(({ type }) => type === ruleType);

/**
 * Finds whether the given validation object contains the required validation rule type.
 *
 * @param {IValidationSchema} validation Validation schema object
 * @return {boolean} Returns true if the validation has the required validation rule type
 */
export const hasRequiredValidation = (validation?: IValidationSchema): boolean =>
  hasValidationType(ValidationRuleType.RTE_REQUIRED, validation) ||
  hasValidationType(ValidationRuleType.REQUIRED, validation);

/**
 * Get element schema in a given page by element id
 *
 * @param {IPageSchema} pageSchema Page schema
 * @param {string} id Id of the element
 * @return {Optional<IElementSchema>} Schema of the element
 */
export const getElementSchemaInPageById = (pageSchema: IPageSchema, id: string): Optional<IElementSchema> => {
  let elementSchema: Optional<IElementSchema>;
  const getSchema = (element: IElementSchema) => {
    if (element.id === id) {
      elementSchema = element;
      return true;
    }

    if (element.type === FormComponentType.Address) {
      if (element.validation && element.validation?.rules.length === 0) {
        return true;
      }
    }

    return element.elements?.some(getSchema);
  };

  pageSchema.elements.some(getSchema);

  return elementSchema;
};

/**
 * Get element schema by element id
 *
 * @param {IFormSchema} formSchema Form schema
 * @param {string} id Id of the element
 * @return {Optional<IElementSchema>} Schema of the element
 */
export const getElementSchemaById = (formSchema: IFormSchema, id: string): Optional<IElementSchema> => {
  let elementSchema: Optional<IElementSchema>;

  formSchema.sections.some((section: ISectionSchema) => {
    return section.pages.some((page: IPageSchema) => {
      elementSchema = getElementSchemaInPageById(page, id);
      return !!elementSchema;
    });
  });

  return elementSchema;
};

/**
 * Get panel element list if panelId is provided. Otherwise get page element list.
 *
 * @param {IPageSchema} page Page schema
 * @param {string} panelId Optional panel id
 * @return {IElementSchema[]} Element list
 */
export const getPanelOrPageElements = (page: IPageSchema, panelId?: string): IElementSchema[] => {
  if (panelId) {
    const panel = getElementSchemaInPageById(page, panelId);
    if (!panel || !Array.isArray(panel.elements)) {
      throw new Error(`The required panel or panel.elements can not be found, panel id: ${panelId}`);
    }
    return panel.elements;
  }

  return page.elements;
};

/**
 * Get the immediate parent of the given child and in a given page.
 *
 * @param {IPageSchema} page Page schema
 * @param {string} id Id of the child element
 * @return {Optional<IPageSchema | IElementSchema>} Parent element which is either a page or a panel
 */
export const getParentOfElementIdForPage = (page: IPageSchema, id: string): Optional<IPageSchema | IElementSchema> => {
  const getParent = (parent: IPageSchema | IElementSchema, id: string) => {
    const foundChild = !!parent.elements?.find(({ id: _id }) => _id === id);
    if (foundChild) {
      return parent;
    }

    let _parent;
    parent.elements
      ?.filter((element) => Array.isArray(element.elements))
      .some((panel) => {
        _parent = getParent(panel, id);
        return !!_parent;
      });

    return _parent;
  };

  return getParent(page, id);
};

export const getElementNameMapForFormSchema = (schema: IFormSchema, excludeFormKeyName?: string): Set<string> => {
  return schema.sections.reduce((acc, section: ISectionSchema) => {
    return section.pages.reduce((acc, page: IPageSchema) => {
      return page.elements.reduce((acc, element: IElementSchema) => {
        if (element.type === FormComponentType.Panel && element?.elements) {
          element.elements.forEach((nestedEl: IElementSchema) => {
            if (excludeFormKeyName !== nestedEl.name) {
              acc.add(nestedEl.name);
            }
          });
        }

        if (excludeFormKeyName === element.name) {
          return acc;
        }

        acc.add(element.name);
        return acc;
      }, acc);
    }, acc);
  }, new Set<string>());
};

/**
 * Get the next unique number for the field name with `keyPrefix` at `atPanelId`. If the key does not exists,
 * then the next number will be undefined. Otherwise the number will be the next number for given prefix
 * starting at 2.
 * @example
 * key_map = {
 *   firstName: null,
 *   address: {
 *     street1: null,
 *     address_more: {
 *       city: null,
 *     },
 *   },
 *   label1: null,
 * }
 *
 * 'lastName' => undefined
 * 'firstName' => 2
 * 'label1' => 2
 *
 * @param {object} schema Form schema data as json
 * @param {string} keyPrefix Name field prefix (i.e. question)
 * @param {string} atPanelId Form schema data as json
 * @returns {number} The next number
 */
export const getNextKeyNumberForFormSchemaWithPrefix = (schema: IFormSchema, keyPrefix: string): Optional<number> => {
  if (!keyPrefix.trim().length) {
    throw Error('Required parameter keyPrefix is empty');
  }

  const formKeys = getElementNameMapForFormSchema(schema);
  if (!formKeys.has(keyPrefix)) {
    return undefined;
  }

  let count = 2;

  while (formKeys.has(`${keyPrefix}${count}`)) {
    count++;
  }

  return count;
};

/**
 * Converts the given string to a field id pattern
 *
 * @example
 * "What's your name?" -> "what_s_your_name"
 * "How many Honors / Pre-AP courses have you taken?" -> "how_many_honors_pre_ap_courses_have_you_taken"
 *
 * @param {string} label The string to be converted to field id pattern
 * @returns {string}
 */
export const convertToFieldId = (label: string): string => {
  return label
    .toLowerCase()
    .replace(/\W+/g, '_') // convert non a-z, 0-9 or _ characters to _
    .replace(/_{2,}/g, '_') // convert 2 or more _ to a single _
    .replace(/(^[^a-z]+)|(^_)|(_$)/g, ''); // remove all non a-z characters at the beginning, remove begin and trailing _
};

/**
 * Returns all the elements across pages
 *
 * @param {IFormSchema} schema Form schema data as json
 * @return {IElementSchema[]} Array of elements
 */
export const getAllComponents = (schema: IFormSchema) => {
  const flatPages = schema.sections.reduce(flattenPages as any, []);
  const elements = flatPages.reduce(flattenElements as any, []);
  return elements;
};

/**
 * Sanitizes the component settings object before saving.
 *
 * @param {IComponentSettings} settings Component settings
 * @return {IComponentSettings} sanitized component settings
 */
export const sanitizeComponentSettings = (settings: IComponentSettings) => {
  return Object.keys(settings).reduce((acc: { [key: string]: any }, key) => {
    const value = settings[key as keyof IComponentSettings];
    acc[key as keyof IComponentSettings] = typeof value === 'string' ? value.trim() : value;
    return acc;
  }, {} as { [key: string]: any }) as IComponentSettings;
};

/**
 * Check if `targetFieldName` is referenced in the given element
 *
 * @param {IElementSchema} element Element
 * @param {string} targetFieldName form field name
 * @return {boolean} a flag to indicate whether `targetFieldName` is referenced in the given element
 */
export const hasFieldNameInVisibleSchema = (element: IElementSchema, targetFieldName: string) => {
  const { visible: { fieldName } = { fieldName: '' } } = element;
  return !!fieldName && fieldName.split('.').includes(targetFieldName);
};

/**
 * Recursively check and delete visible schema for the element with the target field name and all its children
 *
 * @param {IElementSchema[]} elements
 * @param {string} targetFieldName
 * @param {boolean} [shouldDeleteVisibleSchema=false]
 */
function deleteVisibleFieldForElements(
  elements: IElementSchema[],
  targetFieldName: string,
  shouldDeleteVisibleSchema = false
) {
  elements.forEach((element) => {
    const shouldDelete = shouldDeleteVisibleSchema || hasFieldNameInVisibleSchema(element, targetFieldName);
    if (shouldDelete) {
      delete element.visible;
    }
    const { elements } = element;
    if (elements) {
      deleteVisibleFieldForElements(elements, targetFieldName, shouldDelete);
    }
  });
}

/**
 * Deletes the form schema field which references the provided `targetFieldName`
 * This mutates the given `schema` object
 *
 * @param {IFormSchema} schema Form schema
 * @param {string} targetFieldName form field name
 * @return {IFormSchema} mutated form schema
 */
export const deleteSchemaFieldWithReference = (schema: IFormSchema, targetFieldName: string) => {
  // This logic can include invalidating validation field if the validation contains `targetFieldName` in the future.
  schema.sections.forEach(({ pages }) =>
    pages.forEach(({ elements }) => deleteVisibleFieldForElements(elements, targetFieldName))
  );
  return schema;
};

/**
 * Generates an object with a key as a path to the element and the value as an element schema for the given elements array
 *
 * @param {{ [key: string]: IElementSchema }} obj initial object
 * @param {IElementSchema[]} elements array of element schema
 * @param {string} prefix a prefix to prepend to the key
 * @param {string} titlePrefix a prefix to prepend to the element title
 * @returns {{ [key: string]: IElementSchema }}
 */
export const createElementMapByPaths = (
  obj: { [key: string]: IElementSchema },
  elements: IElementSchema[],
  prefix = '',
  titlePrefix = ''
) => {
  elements
    .filter((item) => VISIBLE_COMPONENT_TYPES.includes(item.type))
    .forEach((element) => {
      const { name, elements, title } = element;
      const path = prefix ? `${prefix}${PATH_SEPARATOR}${name}` : name;
      const titlePath = prefix ? `${titlePrefix}${PATH_SEPARATOR}${title}` : title;
      if (elements) {
        createElementMapByPaths(obj, elements, path, titlePath);
      } else if (!obj[path]) {
        element.titlePath = titlePath;
        obj[path] = element;
      }
    });
  return obj;
};

/**
 * Adapt `Profile Info` component field data to IUserProfile
 *
 * @param {{ [key: string]: any }} data `Profile Info` component field data
 * @param {string} data.first_name - The first name of the user.
 * @param {string} data.middle_name - The middle name of the user.
 * @param {string} data.last_name - The last name of the user.
 * @returns {Partial<IUserProfile>}
 */
export const convertProfileInfoFormDataToUserProfile = (data: { [key: string]: any }): Partial<IUserProfile> => ({
  firstName: data['first_name'],
  middleName: data['middle_name'],
  lastName: data['last_name'],
});

const convertNullToUndefined = (val: any) => (val !== null ? val : undefined);

/**
 * Adapt IUserProfile to `Profile Info` component field data
 *
 * @param {{ [key: string]: any }} userProfile - User profile data
 * @param {string} userProfile.firstName - The first name of the user.
 * @param {string} userProfile.middleName - The middle name of the user.
 * @param {string} userProfile.lastName - The last name of the user.
 * @returns {{ [key: string]: any }}
 */
export const convertUserProfileToProfileInfoFormData = ({
  firstName,
  middleName,
  lastName,
}: IUserProfile): { [key: string]: any } => ({
  ['first_name']: convertNullToUndefined(firstName),
  ['middle_name']: convertNullToUndefined(middleName),
  ['last_name']: convertNullToUndefined(lastName),
});

/**
 * Generates element data source for the given master table url
 *
 * @param {string} url Master table api url
 * @returns {IElementDataSource}
 */
export const generateElementDataSourceForMasterTableUrl = (url: string): IElementDataSource => {
  return {
    url: extractPathAndQuery(url),
    valueName: 'id',
    titleName: 'displayText',
  };
};

export enum EnvironmentPath {
  Dev6 = '/dev6',
  Prod = '/prod',
}

/**
 * Extracts the pathname and query string from a URL, removing any instances of 'dev6' or 'prod'.
 * @param url - The full URL to be processed.
 * @returns A string containing only the path and query components of the URL.
 */
const extractPathAndQuery = (url: string): string => {
  const parsedUrl = new URL(url);

  return `${parsedUrl.pathname}${parsedUrl.search}`.replace(EnvironmentPath.Dev6, '').replace(EnvironmentPath.Prod, '');
};

/**
 * Constructs the full data source URL based on the provided API URL.
 * If the URL starts with 'http', it strips the host, leaving only the path and query.
 * @param apiUrl - The base API URL or null/undefined.
 * @returns A full URL by combining API_URL and the processed path and query.
 */
export const constructDataSourceUrl = (apiUrl: string | null | undefined): string => {
  if (apiUrl?.startsWith(FP_API_URL) || apiUrl?.startsWith(API_URL)) {
    return apiUrl;
  }

  let pathAndQuery = apiUrl;
  if (apiUrl && apiUrl.startsWith('http')) {
    pathAndQuery = extractPathAndQuery(apiUrl);
  }
  return `${API_URL}${pathAndQuery}`;
};

/**
 * Generate TreeData for TreeSelectComponent
 * @param options
 */
export const generateTreeData = (options: PathOption[]) => {
  const result: TreeNodeOption[] = [];
  options.forEach(({ labelPath, valuePath }) => {
    if (valuePath.indexOf(PATH_SEPARATOR) === -1 || labelPath.indexOf(PATH_SEPARATOR) === -1) {
      result.push({
        title: labelPath,
        value: valuePath,
        key: valuePath,
        disabled: false,
        selectable: true,
      });
      return;
    }
    const keyOfTreeRoot = valuePath.split(PATH_SEPARATOR)[0];

    const labelFragments = labelPath.split(PATH_SEPARATOR);
    const valueFragments = valuePath.split(PATH_SEPARATOR);
    for (let i = 0; i < valueFragments.length; i++) {
      const objIndex = result.findIndex((item) => item.key === keyOfTreeRoot);
      if (objIndex > -1) {
        if (result[objIndex].children !== undefined) {
          if (result[objIndex].children?.findIndex((item) => item.value === valuePath) === -1) {
            (result[objIndex].children as TreeNodeOption[]).push({
              title: labelFragments[1],
              disabled: false,
              selectable: true,
              key: valuePath,
              value: valuePath,
            });
          }
        } else {
          // first child in the container
          result[objIndex].children = [
            {
              title: labelFragments[1],
              disabled: false,
              selectable: true,
              key: valuePath,
              value: valuePath,
            },
          ];
        }
      } else {
        //new container
        result.push({
          title: labelFragments[0],
          disabled: true,
          selectable: false,
          key: valueFragments[0],
        });
      }
    }
  });
  return result;
};

/**
 * Returns an array of `FormComponentType` that's available to generate form for the given `stageType`.
 *
 * @export
 * @param {StageType} stageType
 * @returns
 */
export function getAvailableComponentTypes(stageType: StageType) {
  return AVAILABLE_COMPONENT_TYPES_BY_STAGE_TYPE.get(stageType);
}
